import React, {useEffect, useMemo, useReducer, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faGlobe, faCoins, faClock, faLocationDot, faUmbrellaBeach, faDownLong, faUpLong,
  faMapLocationDot, faRoute, faSackDollar, faCarSide, faCreditCard, faMap,
  faSignOutAlt, faCircleInfo, faCalendarXmark, faFileContract
} from '@fortawesome/free-solid-svg-icons';
import {faStripe} from '@fortawesome/free-brands-svg-icons';
import {Button, Form, InputGroup, ListGroup, Modal} from '@themesberg/react-bootstrap';
import {FormattedMessage} from 'react-intl';
import Countries from '../components/Countries';
import API from '../services/API';
import {useHistory} from 'react-router-dom';
import Utils from '../services/Utils';
import {usePlan} from '../services/PlanContext';
import AddressSelect from '../components/AddressSelect';
import Geocoding from '../services/Geocoding';
import TimezoneSelect from 'react-timezone-select';
import MapModal from '../components/MapModal';
import ItemStatus from '../components/ItemStatus';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import enLocale from '@fullcalendar/core/locales/en-gb';
import esLocale from '@fullcalendar/core/locales/es';
import itLocale from '@fullcalendar/core/locales/it';
import deLocale from '@fullcalendar/core/locales/de';
import plLocale from '@fullcalendar/core/locales/pl';

// ── Onglets segmentés ─────────────────────────────────────────────
const TABS = [
  {key: 'general', label: 'SETTINGS_TAB_GENERAL'},
  {key: 'pricing', label: 'SETTINGS_TAB_PRICING'},
  {key: 'messages', label: 'SETTINGS_TAB_MESSAGES'},
  {key: 'payments', label: 'SETTINGS_TAB_PAYMENTS'},
  {key: 'legal', label: 'SETTINGS_TAB_LEGAL'},
];

// Forme des CGV (cf. backend CgvType) : texte affiché dans une fenêtre, ou lien vers une page.
const CGV_TYPES = [
  {key: 'TEXT', label: 'CGV_TYPE_TEXT'},
  {key: 'URL', label: 'CGV_TYPE_URL'},
];

// Champs comparés pour le compteur « modifications non sauvegardées ».
const EDITABLE_KEYS = [
  'url', 'defaultLanguage', 'timeZone', 'restrictedCountry', 'currency', 'lengthUnit',
  'bookingBufferTime', 'msgHolidayMode', 'vacationDays', 'msgVehicleBooked', 'minDistance', 'maxDistance',
  'msgErrorMinDistance', 'msgErrorMaxDistance', 'allowedRegionCoords', 'msgErrorAllowedRegion',
  'homeCityName', 'homeCityCoords', 'homeCityPrice', 'homeCityDistance',
  'checkboxDriverPayments', 'checkboxOnlinePayments', 'checkboxCountry', 'checkboxMinDistance',
  'checkboxMaxDistance', 'checkboxAllowedRegion', 'checkboxHomeCity', 'checkboxRoundPrice',
  'checkboxHolidayMode', 'checkboxCgv', 'cgvType', 'cgvUrl', 'cgvText',
];

const normalize = (v) => (v === undefined || v === null) ? '' : (typeof v === 'boolean' ? v : String(v));

// Locale FullCalendar par langue de l'app (calendrier des jours d'absence).
const CALENDAR_LOCALES = {
  fr: frLocale, en: enLocale, es: esLocale,
  it: itLocale, de: deLocale, pl: plLocale
};

// ── Interrupteur (switch) ─────────────────────────────────────────
const Switch = ({checked, onChange, disabled, id}) => (
  <button type="button" role="switch" aria-checked={!!checked} id={id}
          className={`ccv2-switch ${checked ? 'is-on' : ''}`}
          onClick={() => !disabled && onChange()} disabled={disabled}>
    <span className="ccv2-switch-knob"/>
  </button>
);

// ── Carte de paramétrage à icône (+ interrupteur / état désactivé) ─
const SettingCard = ({icon, title, description, full, toggle, disabled, hint, intl, children}) => {
  const isDisabled = toggle ? !toggle.checked : !!disabled;
  return (
    <div className={`ccv2-card ${full ? 'is-full' : ''}`}>
      <div className="ccv2-card-head">
        <div className="ccv2-card-icon"><FontAwesomeIcon icon={icon}/></div>
        <div className="ccv2-card-titles">
          <div className="ccv2-card-title">{title}</div>
          {description && <div className="ccv2-card-desc">{description}</div>}
        </div>
        {toggle && (
          <div className="ccv2-card-switch">
            <span className={`ccv2-switch-label ${toggle.checked ? 'is-on' : ''}`}>
              {intl.formatMessage({id: toggle.checked ? 'SETTINGS_ENABLED' : 'SETTINGS_DISABLED'})}
            </span>
            <Switch checked={toggle.checked} onChange={toggle.onChange} id={toggle.id}/>
          </div>
        )}
      </div>
      <div className={`ccv2-card-body ${isDisabled ? 'is-disabled' : ''}`}>
        {children}
      </div>
      {isDisabled && hint && (
        <div className="ccv2-card-hint">
          <FontAwesomeIcon icon={faCircleInfo} className="ccv2-card-hint-icon"/>
          <span>{hint}</span>
        </div>
      )}
    </div>
  );
};

// ── Champ (libellé + contrôle) ────────────────────────────────────
const Field = ({label, htmlFor, className, children}) => (
  <div className={`ccv2-field ${className || ''}`}>
    {label && <label className="ccv2-field-label" htmlFor={htmlFor}>{label}</label>}
    {children}
  </div>
);

export default (props) => {

  const formReducer = (state, event) => {
    if (event.name || event.value) {
      return {...state, [event.name]: event.value};
    }
    return event;
  };

  const {intl} = props;
  const fm = (id) => intl.formatMessage({id});
  const history = useHistory();
  const {onlinePaymentDisabled, stripeConnected, refresh: refreshPlan} = usePlan();
  // Le paiement en ligne exige une formule payante ET un compte Stripe Connect (qui encaisse) : sans
  // compte, l'interrupteur est verrouillé et le backend refuse de toute façon l'option.
  const onlinePaymentLocked = onlinePaymentDisabled || !stripeConnected;

  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useReducer(formReducer, {});
  const [initialData, setInitialData] = useState(null);
  const [lengthUnit, setLengthUnit] = useState('km');
  const [currentLengthUnit, setCurrentLengthUnit] = useState('km');
  const [saving, setSaving] = useState(false);
  const [showStripeDisconnectModal, setShowStripeDisconnectModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [loadingManageStripe, setLoadingManageStripe] = useState(false);

  // État du compte Stripe Connect, lu chez Stripe (GET /admin/stripe-infos) dès que le compte est
  // rattaché. L'ancien affichage lisait un localStorage que plus rien n'écrivait depuis la refonte :
  // la carte ne pouvait plus montrer que « non connecté ». null = pas encore chargé ou indisponible.
  const [stripeStatus, setStripeStatus] = useState(null);
  useEffect(() => {
    if (!stripeConnected) {
      setStripeStatus(null);
      return;
    }
    API.getStripeInfos(history).then(response => {
      if (response && response.data && response.data.success === true) {
        setStripeStatus(response.data.data);
      } else {
        setStripeStatus(null);
      }
    }).catch(() => setStripeStatus(null));
  }, [stripeConnected, history]);
  const stripeInfos = stripeStatus ? [
    {id: 1, title: fm('STRIPE_ACCOUNT'), statusKey: stripeStatus.active === true ? 'active' : 'inactive'},
    {id: 2, title: fm('CARD_PAYMENTS'), statusKey: stripeStatus.cardPayments},
  ] : [];

  useEffect(() => {
    if (!Utils.isAccessToken(history)) {
      return;
    }

    API.getSettings(history).then(response => {
      if (response && response.data.success === true) {
        setFormData(response.data.data);
        setInitialData({...response.data.data});
        setLengthUnit(response.data.data.lengthUnit === 'MILE' ? 'mi' : 'km');
        setCurrentLengthUnit(response.data.data.lengthUnit === 'MILE' ? 'mi' : 'km');
      } else {
        Utils.notifyError(fm('ERROR'), fm('ERROR_OCCURRED'));
      }
    });

    // Adresse de départ des frais d'approche : comme la réservation manuelle, on passe par la clé
    // Google Maps du chauffeur s'il l'a renseignée, sinon Geocoding retombe sur TomTom.
    API.getModule(history).then(response => {
      if (response && response.data.success === true) {
        Geocoding.configureGoogle(response.data.data.googleMapsApi);
      }
    });
  }, [history, intl]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Compteur de modifications non sauvegardées ──
  const dirtyCount = useMemo(() => {
    if (!initialData) return 0;
    let n = 0;
    for (const k of EDITABLE_KEYS) {
      if (normalize(formData[k]) !== normalize(initialData[k])) n++;
    }
    return n;
  }, [formData, initialData]);

  // ── Handlers ──
  const handleChange = (event) => {
    const isCheckbox = event.target.type === 'checkbox';
    setFormData({name: event.target.id, value: isCheckbox ? event.target.checked : event.target.value});
  };

  const handleToggle = (id) => setFormData({name: id, value: !formData[id]});

  // ── Jours de congé (mode vacances « par jour ») ──
  // Stockés dans formData.vacationDays comme une liste de dates ISO (YYYY-MM-DD) séparées par des
  // virgules — même format que la colonne settings.vacation_days et que l'attribut blockedDates du widget.
  const toIsoDay = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  // « Aujourd'hui » se calcule dans le fuseau du module (= fuseau métier du chauffeur), pas celui
  // du navigateur de l'admin : sinon, à cheval sur minuit, validRange/élagage seraient décalés d'un
  // jour pour un admin connecté depuis un autre fuseau. La sélection (dateClick.dateStr) reste une
  // date calendaire nue, cohérente avec la façon dont le serveur interprète les jours (TZ module).
  const todayIso = useMemo(() => {
    if (formData.timeZone) {
      try {
        return new Intl.DateTimeFormat('en-CA', {
          timeZone: formData.timeZone, year: 'numeric', month: '2-digit', day: '2-digit'
        }).format(new Date());
      } catch (e) { /* fuseau invalide → repli navigateur */ }
    }
    return toIsoDay(new Date());
  }, [formData.timeZone]); // eslint-disable-line react-hooks/exhaustive-deps
  const vacationDays = useMemo(
    () => (formData.vacationDays || '').split(',').map(s => s.trim()).filter(Boolean),
    [formData.vacationDays]
  );
  const vacationEvents = useMemo(
    () => vacationDays.map(d => ({start: d, allDay: true, display: 'background', backgroundColor: '#F87171'})),
    [vacationDays]
  );
  const handleToggleVacationDay = (info) => {
    if (info.dateStr < todayIso) return; // on ne bloque pas de jours passés
    const set = new Set(vacationDays);
    if (set.has(info.dateStr)) set.delete(info.dateStr); else set.add(info.dateStr);
    // On élague au passage les jours passés (devenus inutiles) pour ne pas accumuler de vieilles dates.
    setFormData({name: 'vacationDays', value: Array.from(set).filter(d => d >= todayIso).sort().join(',')});
  };
  const clearVacationDays = () => setFormData({name: 'vacationDays', value: ''});
  const getCalendarLocale = () => CALENDAR_LOCALES[Utils.getLanguage().toLowerCase()] || CALENDAR_LOCALES.fr;

  const handleChangeTimeZone = (event) => setFormData({name: 'timeZone', value: event.value});

  const handleChangeLengthUnit = (event) => {
    setFormData({name: event.target.id, value: event.target.value});
    setLengthUnit(event.target.value === 'MILE' ? 'mi' : 'km');
  };

  const getBookingBufferTimeValue = (rawValue) => rawValue ? rawValue.substring(0, rawValue.length - 1) : '0';
  const getBookingBufferTimeCharValue = (rawValue) => rawValue ? rawValue.slice(-1) : 'm';

  const handleBookingBufferTimeChange = (event) => {
    const char = getBookingBufferTimeCharValue(formData.bookingBufferTime);
    setFormData({name: 'bookingBufferTime', value: event.target.value + char});
  };
  const handleBookingBufferTimeCharChange = (event) => {
    const timeValue = getBookingBufferTimeValue(formData.bookingBufferTime);
    setFormData({name: 'bookingBufferTime', value: timeValue + event.target.value});
  };

  // Libellé et coordonnées sont posés ensemble à la sélection : plus de résolution différée au moment
  // d'enregistrer (l'ancienne recherche de ville perdait les coordonnées si la liste avait changé).
  // Un dispatch par champ : la sélection arrive après un await (résolution Google), le reducer fusionne
  // donc sur l'état courant plutôt que sur un formData capturé avant.
  const handleSelectHomeAddress = ({label, lat, lon}) => {
    setFormData({name: 'homeCityName', value: label});
    setFormData({name: 'homeCityCoords', value: lat + ',' + lon});
    setFormData({name: 'homeCityPrecise', value: true});
  };

  const handleShowMapModal = () => setShowMapModal(true);
  const setAllowedRegionCoords = (coords) => setFormData({name: 'allowedRegionCoords', value: coords});

  const handleStripeManage = () => {
    setLoadingManageStripe(true);
    API.getStripeUrl(history).then(response => {
      if (response && response.data.success === true) {
        window.location.replace(response.data.data);
      } else {
        setLoadingManageStripe(false);
        Utils.notifyError(fm('ERROR'), fm('ERROR_OCCURRED'));
      }
    });
  };

  const handleStripeDisconnect = () => {
    API.getStripeDisconnect(history).then(response => {
      if (response && response.data.success === true) {
        Utils.notifySuccess(fm('OK'), fm('STRIPE_DISCONNECTED'));
        setStripeStatus(null);
        // Le backend a coupé le paiement en ligne : on aligne le formulaire et le verrou de l'option.
        // Un dispatch par champ : un objet sans `name` remplacerait tout le formulaire (cf. formReducer).
        setFormData({name: 'checkboxOnlinePayments', value: false});
        setFormData({name: 'checkboxDriverPayments', value: true});
        // Déjà enregistré côté serveur : ce n'est pas une modification en attente.
        setInitialData(prev => prev && {...prev, checkboxOnlinePayments: false, checkboxDriverPayments: true});
        refreshPlan && refreshPlan();
      } else {
        Utils.notifyError(fm('ERROR'), fm('ERROR_OCCURRED'));
      }
      setShowStripeDisconnectModal(false);
    });
  };

  const handleCancel = () => {
    if (!initialData) return;
    setFormData({...initialData});
    setLengthUnit(initialData.lengthUnit === 'MILE' ? 'mi' : 'km');
  };

  const handleSave = (e) => {
    if (e) e.preventDefault();

    if (formData.checkboxCountry && !formData.restrictedCountry) {
      formData.restrictedCountry = 'FR';
    }

    if (!formData.timeZone) {
      formData.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    // Validation explicite (remplace la validation HTML native, peu fiable avec
    // les panneaux d'onglets masqués) : on bascule sur l'onglet fautif.
    const isEmpty = (v) => v === '' || v === null || v === undefined;
    if (isEmpty(formData.url)) {
      Utils.notifyWarning(fm('ERROR'), fm('SETTINGS_URL_REQUIRED'));
      setActiveTab('general');
      return;
    }
    if ((formData.checkboxMinDistance && isEmpty(formData.minDistance)) ||
      (formData.checkboxMaxDistance && isEmpty(formData.maxDistance)) ||
      (formData.checkboxHomeCity && (isEmpty(formData.homeCityPrice) || isEmpty(formData.homeCityDistance)))) {
      Utils.notifyWarning(fm('ERROR'), fm('SETTINGS_FILL_REQUIRED'));
      setActiveTab('pricing');
      return;
    }

    if (formData.checkboxHomeCity && !formData.homeCityCoords) {
      Utils.notifyWarning(fm('HOMECITY_NOT_FOUND'), fm('NOTIFY_HOMECITY_NOT_FOUND'));
      setActiveTab('pricing');
      return;
    }
    if (!formData.checkboxDriverPayments && !formData.checkboxOnlinePayments) {
      Utils.notifyWarning(fm('PAYMENT_METHOD_NOT_SELECTED'), fm('PLEASE_TICK_ONE_PAYMENT_METHOD'));
      setActiveTab('payments');
      return;
    }
    const cgvType = formData.cgvType || 'TEXT';
    if (formData.checkboxCgv && isEmpty(((cgvType === 'URL' ? formData.cgvUrl : formData.cgvText) || '').trim())) {
      Utils.notifyWarning(fm('ERROR'), fm('CGV_CONTENT_REQUIRED'));
      setActiveTab('legal');
      return;
    }
    if (currentLengthUnit !== lengthUnit) {
      Utils.notify(fm('LENGTH_UNIT_CHANGED'), fm('NOTIFY_LENGTH_UNIT'));
    }

    setSaving(true);
    API.postSettings(history, formData).then(response => {
      setSaving(false);
      if (response && response.data.success === true) {
        Utils.notifySuccess(fm('OK'), fm('SAVED_CHANGES'));
        setCurrentLengthUnit(lengthUnit);
        setInitialData({...formData});
      } else if (response && response.data && ['CGV_CONTENT_REQUIRED', 'CGV_INVALID_URL'].includes(response.data.data)) {
        // Rien n'a été enregistré : le contenu des CGV activées est inexploitable.
        Utils.notifyWarning(fm('ERROR'), fm(response.data.data));
        setActiveTab('legal');
      } else if (response && response.data && response.data.data === 'STRIPE_ACCOUNT_NOT_CREATED') {
        // Tout est enregistré sauf le paiement en ligne, refusé faute de compte Stripe.
        Utils.notifyWarning(fm('ONLINE_PAYMENTS'), fm('ONLINE_PAYMENT_STRIPE_REQUIRED_SAVED'));
        setFormData({name: 'checkboxOnlinePayments', value: false});
        setFormData({name: 'checkboxDriverPayments', value: true});
        setCurrentLengthUnit(lengthUnit);
        setInitialData({...formData, checkboxOnlinePayments: false, checkboxDriverPayments: true});
      } else {
        Utils.notifyError(fm('ERROR'), fm('ERROR_OCCURRED'));
      }
    });
  };

  // ── Données dérivées ──
  const currencyIcon = Utils.getCurrencyIcon(formData.currency);
  const distanceUnitHint = ' (' + lengthUnit + ')';

  const panelProps = (key) => ({
    id: `ccv2-panel-${key}`,
    role: 'tabpanel',
    'aria-labelledby': `ccv2-tab-${key}`,
    className: `ccv2-settings-panel ${activeTab === key ? '' : 'd-none'}`,
  });

  return (
    <div className="ccv2-settings">

      {/* En-tête de page */}
      <div className="ccv2-settings-head">
        <div>
          <h1 className="ccv2-settings-title"><FormattedMessage id="SETTINGS_TITLE"/></h1>
          <p className="ccv2-settings-subtitle"><FormattedMessage id="SETTINGS_SUBTITLE"/></p>
        </div>
      </div>

      {/* Onglets segmentés */}
      <div className="ccv2-segmented" role="tablist">
        {TABS.map(tab => (
          <button key={tab.key} type="button" role="tab" id={`ccv2-tab-${tab.key}`}
                  aria-selected={activeTab === tab.key} aria-controls={`ccv2-panel-${tab.key}`}
                  className={`ccv2-seg-tab ${activeTab === tab.key ? 'is-active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}>
            {intl.formatMessage({id: tab.label})}
          </button>
        ))}
      </div>

      <div className="ccv2-settings-body">

        {/* ────────────── GÉNÉRAL ────────────── */}
        <div {...panelProps('general')}>
          <div className="ccv2-settings-grid">

            <SettingCard icon={faGlobe} full intl={intl}
                         title={fm('SETTINGS_CARD_IDENTITY_TITLE')} description={fm('SETTINGS_CARD_IDENTITY_DESC')}>
              <div className="ccv2-field-grid">
                <Field className="is-wide" label={fm('YOUR_WEBSITE_URL')} htmlFor="url">
                  <Form.Control required type="text" id="url" placeholder="https://..."
                                onChange={handleChange} value={formData.url || ''}/>
                </Field>
                <Field label={fm('DEFAULT_LANGUAGE')} htmlFor="defaultLanguage">
                  <Form.Select id="defaultLanguage" onChange={handleChange} value={formData.defaultLanguage || 'fr'}>
                    <option value="fr">{fm('FRENCH')}</option>
                    <option value="en">{fm('ENGLISH')}</option>
                    <option value="es">{fm('SPANISH')}</option>
                    <option value="it">{fm('ITALIAN')}</option>
                    <option value="de">{fm('GERMAN')}</option>
                    <option value="pl">{fm('POLISH')}</option>
                  </Form.Select>
                </Field>
                <Field label={fm('TIME_ZONE')} htmlFor="timeZone">
                  <div className="ccv2-tz">
                    <TimezoneSelect
                      inputId="timeZone"
                      value={formData.timeZone ? formData.timeZone : Intl.DateTimeFormat().resolvedOptions().timeZone}
                      onChange={handleChangeTimeZone}/>
                  </div>
                </Field>
              </div>
            </SettingCard>

            <SettingCard icon={faCoins} intl={intl}
                         title={fm('SETTINGS_CARD_CURRENCY_TITLE')} description={fm('SETTINGS_CARD_CURRENCY_DESC')}>
              <div className="ccv2-field-grid">
                <Field label={fm('CURRENCY')} htmlFor="currency">
                  <Form.Select id="currency" onChange={handleChange} value={formData.currency || 'EUR'}>
                    {formData.availableCurrencies && formData.availableCurrencies.map(currency => (
                      <option key={currency} value={currency}>{fm(currency)}</option>
                    ))}
                  </Form.Select>
                </Field>
                <Field label={fm('LENGTH_UNIT')} htmlFor="lengthUnit">
                  <Form.Select id="lengthUnit" onChange={handleChangeLengthUnit} value={formData.lengthUnit || 'KM'}>
                    <option value="KM">km</option>
                    <option value="MILE">mile</option>
                  </Form.Select>
                </Field>
              </div>
            </SettingCard>

            <SettingCard icon={faClock} intl={intl}
                         title={fm('SETTINGS_CARD_DELAY_TITLE')} description={fm('SETTINGS_CARD_DELAY_DESC')}>
              <div className="ccv2-field-grid">
                <Field label={fm('SETTINGS_DELAY_MIN')} htmlFor="bookingBufferTime">
                  <Form.Control required type="number" min="0" id="bookingBufferTime"
                                onChange={handleBookingBufferTimeChange}
                                value={getBookingBufferTimeValue(formData.bookingBufferTime)}/>
                </Field>
                <Field label={fm('SETTINGS_DELAY_UNIT')} htmlFor="bookingBufferTimeUnit">
                  <Form.Select id="bookingBufferTimeUnit" onChange={handleBookingBufferTimeCharChange}
                               value={getBookingBufferTimeCharValue(formData.bookingBufferTime)}>
                    <option value="m">{fm('MINUTES')}</option>
                    <option value="h">{fm('HOURS')}</option>
                    <option value="d">{fm('DAYS')}</option>
                  </Form.Select>
                </Field>
              </div>
            </SettingCard>

            <SettingCard icon={faUmbrellaBeach} intl={intl}
                         title={fm('SETTINGS_CARD_HOLIDAY_TITLE')} description={fm('SETTINGS_CARD_HOLIDAY_DESC')}
                         toggle={{checked: !!formData.checkboxHolidayMode, onChange: () => handleToggle('checkboxHolidayMode'), id: 'checkboxHolidayMode'}}>
              <Field label={fm('MSG_FOR_HOLIDAY_MODE')} htmlFor="msgHolidayMode">
                <Form.Control as="textarea" id="msgHolidayMode" disabled={!formData.checkboxHolidayMode}
                              onChange={handleChange} value={formData.msgHolidayMode || ''}/>
              </Field>
            </SettingCard>

            <SettingCard icon={faLocationDot} intl={intl}
                         title={fm('SETTINGS_CARD_COUNTRY_TITLE')} description={fm('SETTINGS_CARD_COUNTRY_DESC')}
                         toggle={{checked: !!formData.checkboxCountry, onChange: () => handleToggle('checkboxCountry'), id: 'checkboxCountry'}}>
              <div className="ccv2-field-grid is-single">
                <Field label={fm('SELECT_COUNTRY_RESTRICTION_AUTOCOMPLETE')} htmlFor="restrictedCountry">
                  <Countries id="restrictedCountry" disabled={!formData.checkboxCountry}
                             onChange={handleChange} value={formData.restrictedCountry} intl={intl}/>
                </Field>
              </div>
            </SettingCard>

            <SettingCard icon={faCalendarXmark} intl={intl}
                         title={fm('SETTINGS_CARD_VACATION_TITLE')} description={fm('SETTINGS_CARD_VACATION_DESC')}>
              <Field label={fm('VACATION_DAYS_LABEL')}>
                <div className="ccv2-vac-calendar">
                  <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    locale={getCalendarLocale()}
                    firstDay={1}
                    height="auto"
                    fixedWeekCount={false}
                    showNonCurrentDates={false}
                    headerToolbar={{start: 'title', center: '', end: 'prev,next'}}
                    dateClick={handleToggleVacationDay}
                    validRange={{start: todayIso}}
                    events={vacationEvents}
                  />
                </div>
                <div className="ccv2-vac-footer">
                  <span className="ccv2-vac-count">
                    {vacationDays.length > 0
                      ? intl.formatMessage({id: 'VACATION_DAYS_COUNT'}, {count: vacationDays.length})
                      : fm('VACATION_DAYS_NONE')}
                  </span>
                  {vacationDays.length > 0 &&
                    <button type="button" className="ccv2-vac-clear" onClick={clearVacationDays}>
                      {fm('VACATION_DAYS_CLEAR')}
                    </button>}
                </div>
              </Field>
            </SettingCard>

          </div>
        </div>

        {/* ────────────── TARIFICATION & DISTANCES ────────────── */}
        <div {...panelProps('pricing')}>
          <div className="ccv2-settings-grid">

            <SettingCard icon={faDownLong} intl={intl}
                         title={fm('SETTINGS_CARD_MINDIST_TITLE')} description={fm('SETTINGS_CARD_MINDIST_DESC')}
                         toggle={{checked: !!formData.checkboxMinDistance, onChange: () => handleToggle('checkboxMinDistance'), id: 'checkboxMinDistance'}}>
              <div className="ccv2-field-grid is-single">
                <Field label={fm('MIN_DISTANCE') + distanceUnitHint} htmlFor="minDistance">
                  <Form.Control required type="number" min="0" id="minDistance" disabled={!formData.checkboxMinDistance}
                                onChange={handleChange} value={formData.minDistance != null ? formData.minDistance : ''}/>
                </Field>
              </div>
            </SettingCard>

            <SettingCard icon={faUpLong} intl={intl}
                         title={fm('SETTINGS_CARD_MAXDIST_TITLE')} description={fm('SETTINGS_CARD_MAXDIST_DESC')}
                         toggle={{checked: !!formData.checkboxMaxDistance, onChange: () => handleToggle('checkboxMaxDistance'), id: 'checkboxMaxDistance'}}>
              <div className="ccv2-field-grid is-single">
                <Field label={fm('MAX_DISTANCE') + distanceUnitHint} htmlFor="maxDistance">
                  <Form.Control required type="number" min="0" id="maxDistance" disabled={!formData.checkboxMaxDistance}
                                onChange={handleChange} value={formData.maxDistance != null ? formData.maxDistance : ''}/>
                </Field>
              </div>
            </SettingCard>

            <SettingCard icon={faMapLocationDot} intl={intl}
                         title={fm('SETTINGS_CARD_REGION_TITLE')} description={fm('SETTINGS_CARD_REGION_DESC')}
                         toggle={{checked: !!formData.checkboxAllowedRegion, onChange: () => handleToggle('checkboxAllowedRegion'), id: 'checkboxAllowedRegion'}}>
              <div className="ccv2-region-row">
                <button type="button" className="ccv2-btn ccv2-btn-secondary"
                        disabled={!formData.checkboxAllowedRegion} onClick={handleShowMapModal}>
                  <FontAwesomeIcon icon={faMap}/> {fm('SELECT_ALLOWED_REGION')}
                </button>
                {formData.allowedRegionCoords &&
                  <span className="ccv2-region-ok">{fm('REGION_SELECTED')}</span>}
              </div>
            </SettingCard>

            <SettingCard icon={faSackDollar} intl={intl}
                         title={fm('SETTINGS_CARD_ROUND_TITLE')} description={fm('SETTINGS_CARD_ROUND_DESC')}
                         toggle={{checked: !!formData.checkboxRoundPrice, onChange: () => handleToggle('checkboxRoundPrice'), id: 'checkboxRoundPrice'}}>
              <div className="ccv2-msg-preview"><FormattedMessage id="SETTINGS_CARD_ROUND_BODY"/></div>
            </SettingCard>

            <SettingCard icon={faRoute} full intl={intl}
                         title={fm('SETTINGS_CARD_APPROACH_TITLE')} description={fm('SETTINGS_CARD_APPROACH_DESC')}
                         toggle={{checked: !!formData.checkboxHomeCity, onChange: () => handleToggle('checkboxHomeCity'), id: 'checkboxHomeCity'}}>
              <div className="ccv2-field-grid">
                <Field label={fm('HOME_CITY')} htmlFor="homeCityName" className="is-wide">
                  <div className="ccv2-address">
                    <AddressSelect id="homeCityName" value={formData.homeCityName} intl={intl}
                                   onSelect={handleSelectHomeAddress} disabled={!formData.checkboxHomeCity}/>
                  </div>
                  {formData.checkboxHomeCity && formData.homeCityName && !formData.homeCityPrecise && (
                    <div className="ccv2-card-hint">
                      <FontAwesomeIcon icon={faCircleInfo} className="ccv2-card-hint-icon"/>
                      <span>{fm('HOME_CITY_LEGACY_HINT')}</span>
                    </div>
                  )}
                </Field>
                <Field label={lengthUnit === 'mi' ? fm('PRICE_PER_MILE') : fm('PRICE_PER_KM')} htmlFor="homeCityPrice">
                  <InputGroup>
                    <InputGroup.Text><FontAwesomeIcon icon={currencyIcon}/></InputGroup.Text>
                    <Form.Control required type="number" id="homeCityPrice" step="any" min="0"
                                  disabled={!formData.checkboxHomeCity}
                                  onChange={handleChange} value={formData.homeCityPrice != null ? formData.homeCityPrice : ''}/>
                  </InputGroup>
                </Field>
                <Field label={fm('MIN_DISTANCE_BEFORE_PRICING') + distanceUnitHint} htmlFor="homeCityDistance">
                  <Form.Control required type="number" min="0" id="homeCityDistance" disabled={!formData.checkboxHomeCity}
                                onChange={handleChange} value={formData.homeCityDistance != null ? formData.homeCityDistance : ''}/>
                </Field>
              </div>
            </SettingCard>

          </div>
        </div>

        {/* ────────────── MESSAGES ────────────── */}
        <div {...panelProps('messages')}>
          <div className="ccv2-settings-grid">

            <SettingCard icon={faCarSide} intl={intl}
                         title={fm('SETTINGS_CARD_VEHICLEFULL_TITLE')} description={fm('SETTINGS_CARD_VEHICLEFULL_DESC')}>
              <Field htmlFor="msgVehicleBooked">
                <Form.Control as="textarea" id="msgVehicleBooked"
                              onChange={handleChange} value={formData.msgVehicleBooked || ''}/>
              </Field>
            </SettingCard>

            <SettingCard icon={faDownLong} intl={intl}
                         title={fm('SETTINGS_CARD_MSG_MINDIST_TITLE')} description={fm('SETTINGS_CARD_MSG_MINDIST_DESC')}
                         disabled={!formData.checkboxMinDistance} hint={fm('SETTINGS_MSG_DISABLED_HINT')}>
              <Field htmlFor="msgErrorMinDistance">
                <Form.Control as="textarea" id="msgErrorMinDistance" disabled={!formData.checkboxMinDistance}
                              onChange={handleChange} value={formData.msgErrorMinDistance || ''}/>
              </Field>
            </SettingCard>

            <SettingCard icon={faUpLong} intl={intl}
                         title={fm('SETTINGS_CARD_MSG_MAXDIST_TITLE')} description={fm('SETTINGS_CARD_MSG_MAXDIST_DESC')}
                         disabled={!formData.checkboxMaxDistance} hint={fm('SETTINGS_MSG_DISABLED_HINT')}>
              <Field htmlFor="msgErrorMaxDistance">
                <Form.Control as="textarea" id="msgErrorMaxDistance" disabled={!formData.checkboxMaxDistance}
                              onChange={handleChange} value={formData.msgErrorMaxDistance || ''}/>
              </Field>
            </SettingCard>

            <SettingCard icon={faMapLocationDot} intl={intl}
                         title={fm('SETTINGS_CARD_MSG_REGION_TITLE')} description={fm('SETTINGS_CARD_MSG_REGION_DESC')}
                         disabled={!formData.checkboxAllowedRegion} hint={fm('SETTINGS_MSG_DISABLED_HINT')}>
              <Field htmlFor="msgErrorAllowedRegion">
                <Form.Control as="textarea" id="msgErrorAllowedRegion" disabled={!formData.checkboxAllowedRegion}
                              onChange={handleChange} value={formData.msgErrorAllowedRegion || ''}/>
              </Field>
            </SettingCard>

          </div>
        </div>

        {/* ────────────── PAIEMENTS ────────────── */}
        <div {...panelProps('payments')}>
          <div className="ccv2-settings-grid">

            <SettingCard icon={faCreditCard} full intl={intl}
                         title={fm('SETTINGS_CARD_PAYMETHODS_TITLE')} description={fm('SETTINGS_CARD_PAYMETHODS_DESC')}>
              <div className="ccv2-toggle-row">
                <div>
                  <div className="ccv2-toggle-row-title">{fm('DRIVER_PAYMENTS')}</div>
                  <div className="ccv2-toggle-row-desc">{fm('SETTINGS_DRIVER_PAYMENTS_DESC')}</div>
                </div>
                <Switch checked={!!formData.checkboxDriverPayments} id="checkboxDriverPayments"
                        onChange={() => handleToggle('checkboxDriverPayments')}/>
              </div>
              <div className="ccv2-toggle-row">
                <div>
                  <div className="ccv2-toggle-row-title">{fm('ONLINE_PAYMENTS')}</div>
                  <div className="ccv2-toggle-row-desc">{fm('SETTINGS_ONLINE_PAYMENTS_DESC')}</div>
                </div>
                <Switch checked={!!formData.checkboxOnlinePayments} id="checkboxOnlinePayments"
                        disabled={onlinePaymentLocked}
                        onChange={() => handleToggle('checkboxOnlinePayments')}/>
              </div>
            </SettingCard>

            <SettingCard icon={faStripe} full intl={intl}
                         title={fm('SETTINGS_CARD_STRIPE_TITLE')} description={fm('SETTINGS_CARD_STRIPE_DESC')}
                         disabled={onlinePaymentDisabled}
                         hint={onlinePaymentDisabled ? fm('ONLINE_PAYMENT_FREE_LOCKED')
                           : !stripeConnected ? fm('ONLINE_PAYMENT_STRIPE_REQUIRED')
                           : !formData.checkboxOnlinePayments ? fm('SETTINGS_STRIPE_DISABLED_HINT') : null}>
              {stripeConnected && stripeInfos.length > 0 && <ListGroup className="list-group-flush ccv2-stripe-status">
                {stripeInfos.map(info => <ItemStatus intl={intl} key={`stripe-infos-${info.id}`} {...info} />)}
              </ListGroup>}
              {!stripeConnected && <p className="ccv2-card-desc mb-3"><FormattedMessage id="BANK_DETAILS_INFO"/></p>}
              <div className="ccv2-stripe-actions">
                <Button variant="primary" size="sm" onClick={handleStripeManage} disabled={loadingManageStripe}>
                  {loadingManageStripe && <i className="spinner-border spinner-border-sm text-light"/>}
                  {!loadingManageStripe &&
                    <span>
                      <FontAwesomeIcon icon={faStripe} className="me-1"/>&nbsp;
                      <FormattedMessage id={stripeConnected ? 'MANAGE_BANK_ACCOUNT' : 'CONNECT_MY_ACCOUNT'}/>
                    </span>}
                </Button>
                {(!loadingManageStripe && stripeConnected) &&
                  <Button variant="danger" size="sm" onClick={() => setShowStripeDisconnectModal(true)}>
                    <FontAwesomeIcon icon={faSignOutAlt} className="me-1"/>
                    <FormattedMessage id="DISCONNECT"/>
                  </Button>}
              </div>
            </SettingCard>

          </div>
        </div>

        {/* ────────────── CGV ────────────── */}
        <div {...panelProps('legal')}>
          <div className="ccv2-settings-grid">

            <SettingCard icon={faFileContract} full intl={intl}
                         title={fm('SETTINGS_CARD_CGV_TITLE')} description={fm('SETTINGS_CARD_CGV_DESC')}
                         hint={fm('SETTINGS_CGV_DISABLED_HINT')}
                         toggle={{checked: !!formData.checkboxCgv, onChange: () => handleToggle('checkboxCgv'), id: 'checkboxCgv'}}>
              <div className="ccv2-segmented is-inline" role="radiogroup" aria-label={fm('SETTINGS_CARD_CGV_TITLE')}>
                {CGV_TYPES.map(type => (
                  <button key={type.key} type="button" role="radio"
                          aria-checked={(formData.cgvType || 'TEXT') === type.key}
                          className={`ccv2-seg-tab ${(formData.cgvType || 'TEXT') === type.key ? 'is-active' : ''}`}
                          disabled={!formData.checkboxCgv}
                          onClick={() => setFormData({name: 'cgvType', value: type.key})}>
                    {fm(type.label)}
                  </button>
                ))}
              </div>
              {(formData.cgvType || 'TEXT') === 'URL' ?
                <Field label={fm('CGV_URL_LABEL')} htmlFor="cgvUrl">
                  <Form.Control type="url" id="cgvUrl" placeholder="https://..." disabled={!formData.checkboxCgv}
                                onChange={handleChange} value={formData.cgvUrl || ''}/>
                  <div className="ccv2-field-help">{fm('CGV_URL_HELP')}</div>
                </Field>
                :
                <Field label={fm('CGV_TEXT_LABEL')} htmlFor="cgvText">
                  <Form.Control as="textarea" id="cgvText" className="ccv2-cgv-text" disabled={!formData.checkboxCgv}
                                onChange={handleChange} value={formData.cgvText || ''}/>
                  <div className="ccv2-field-help">{fm('CGV_TEXT_HELP')}</div>
                </Field>
              }
            </SettingCard>

          </div>
        </div>

      </div>

      {/* Barre d'action ancrée */}
      <div className="ccv2-actionbar">
        <div className={`ccv2-actionbar-status ${dirtyCount === 0 ? 'is-clean' : ''}`}>
          <span className="ccv2-actionbar-dot"/>
          {dirtyCount === 0
            ? <span><FormattedMessage id="SETTINGS_UP_TO_DATE"/></span>
            : <span><b>{dirtyCount}</b> <FormattedMessage id="SETTINGS_UNSAVED_SUFFIX" values={{count: dirtyCount}}/></span>}
        </div>
        <div className="ccv2-actionbar-actions">
          <button type="button" className="ccv2-btn ccv2-btn-secondary"
                  onClick={handleCancel} disabled={dirtyCount === 0 || saving}>
            <FormattedMessage id="CANCEL"/>
          </button>
          <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={handleSave} disabled={saving}>
            {saving && <i className="spinner-border spinner-border-sm text-light"/>}
            {!saving && <FormattedMessage id="SAVE"/>}
          </button>
        </div>
      </div>

      <MapModal title={fm('SELECT_ALLOWED_REGION')} coords={formData.allowedRegionCoords}
                showMapModal={showMapModal} setShowMapModal={setShowMapModal} setCoords={setAllowedRegionCoords}/>

      <Modal as={Modal.Dialog} centered show={showStripeDisconnectModal} onHide={() => setShowStripeDisconnectModal(false)}>
        <Modal.Header>
          <Modal.Title className="h6"><FormattedMessage id="DISCONNECT_MY_STRIPE"/></Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => setShowStripeDisconnectModal(false)}/>
        </Modal.Header>
        <Modal.Body><FormattedMessage id="DISCONNECT_MY_STRIPE_INFOS"/></Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleStripeDisconnect}><FormattedMessage id="DISCONNECT"/></Button>
          <Button variant="link" className="text-gray ms-auto" onClick={() => setShowStripeDisconnectModal(false)}>
            <FormattedMessage id="CLOSE"/>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
